import { cn } from '@rocco/utils/cn';
import { SectionLabel } from '../components/SectionLabel';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { ReactComponent as Arrow } from '../images/arrow.svg';

/**
 * Intro / mission statement (Figma `997:34953`, 1920 × 520).
 *
 * A two-tone brand statement — the first sentence in ink, the rest in grey —
 * prefixed by an "(About)" eyebrow that nests into the first line's indent,
 * a leading arrow, and a dark Get Started pill below.
 *
 * Frame-based layout (`aspect-[1920/520]` + absolute children in % / vw) so it
 * scales proportionally and the line breaks stay fixed, matching the B2B
 * landing sections. Mobile relaxes to a flowed stack.
 */
const FRAME_HEIGHT = 520;
const pctX = (x: number) => (x / 1920) * 100;
const pctY = (y: number) => (y / FRAME_HEIGHT) * 100;

const STATEMENT_LEAD = 'Creating a home is one of life’s biggest investments. ';
const STATEMENT_TAIL =
    'ArchiPro helps you find the inspiration, products and professionals to do it with confidence.';

export const IntroSection = ({
    compactCta = false,
}: {
    /** Detail-page closing uses a fixed 145px/16px pill instead of the
     *  homepage's larger vw-scaled Get Started button. */
    compactCta?: boolean;
} = {}) => {
    const { handleCTAClick, handleLoginClick } =
        useCTAHandlers('HomePageIntro');

    const onGetStarted = () => {
        handleCTAClick('startProject');
        handleLoginClick();
    };

    return (
        <section className="relative w-full bg-white text-[#181818]">
            {/* Desktop — absolute-positioned to the 1920 frame. The frame caps
                at the centred 1920 container so the %-anchored children hold
                their design offsets on wider screens. */}
            <div
                className="relative mx-auto w-full max-w-[120rem] max-md:hidden"
                style={{ aspectRatio: `1920 / ${FRAME_HEIGHT}` }}
            >
                {/* Leading arrow */}
                <Arrow
                    className="absolute"
                    style={{
                        left: compactCta
                            ? 'calc(2.3124rem + 1rem)'
                            : `${pctX(25)}%`,
                        top: `${pctY(10)}%`,
                        width: `${pctX(57.84)}%`,
                        height: `${pctY(42.24)}%`,
                    }}
                    aria-hidden="true"
                />

                {/* (About) eyebrow — sits in the first-line indent. On the
                    detail closing it's centred in the statement's first line. */}
                <span
                    className={
                        compactCta
                            ? 'absolute flex items-center md:h-[calc(4.167vw_*_1.07)] lg:h-[calc(5rem_*_1.07)]'
                            : 'absolute'
                    }
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: compactCta ? `${pctY(0)}%` : `${pctY(12.43)}%`,
                    }}
                >
                    <SectionLabel>(About)</SectionLabel>
                </span>

                {/* Two-tone statement */}
                <p
                    className="absolute m-0 font-sans leading-[1.07] font-normal md:indent-[15.36vw] md:text-[4.167vw] md:tracking-[-0.2vw] lg:indent-[18.432rem] lg:text-[5rem] lg:tracking-[-0.24rem]"
                    style={{
                        left: `${pctX(497.59)}%`,
                        top: `${pctY(0)}%`,
                        width: `${pctX(1405)}%`,
                    }}
                >
                    <span>{STATEMENT_LEAD}</span>
                    <span className="text-[#767676]">{STATEMENT_TAIL}</span>
                </p>

                {/* Get Started */}
                <button
                    type="button"
                    onClick={onGetStarted}
                    className={cn(
                        'absolute flex items-center justify-center rounded-full bg-[#333] font-sans font-normal tracking-[-0.03em] whitespace-nowrap text-white shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)]',
                        !compactCta &&
                            'md:px-[1.436vw] md:text-[1.182vw] lg:px-[1.7232rem] lg:text-[1.4184rem]',
                    )}
                    style={{
                        left: compactCta
                            ? `calc(${pctX(497.59)}% + 43px)`
                            : `${pctX(497.59)}%`,
                        top: compactCta
                            ? `calc(${pctY(459.67)}% + 0.5rem)`
                            : `${pctY(459.67)}%`,
                        height: `${pctY(60)}%`,
                        ...(compactCta
                            ? {
                                  width: '9.0625rem',
                                  paddingInline: '1rem',
                                  fontSize: '1rem',
                              }
                            : {
                                  minWidth: `${pctX(230.27)}%`,
                              }),
                    }}
                >
                    Get Started
                </button>
            </div>

            {/* Mobile — (About) nests into the statement's first-line indent,
                matching the Figma about frame (1113:31669). */}
            <div className="relative px-6 md:hidden">
                <span className="absolute top-[0.3881rem] left-6">
                    <SectionLabel>(About)</SectionLabel>
                </span>
                <p
                    className="m-0 font-sans text-[1.3481rem] leading-[1.07] font-normal tracking-[-0.048em]"
                    style={{ textIndent: '4.9318rem' }}
                >
                    <span>{STATEMENT_LEAD}</span>
                    <span className="text-[#767676]">{STATEMENT_TAIL}</span>
                </p>
            </div>
        </section>
    );
};
